import DeviceControls from 'features/Integrations/components/DeviceControls/DeviceControls';
import DeviceHistory from 'features/Integrations/components/DeviceHistory/DeviceHistory';
import BaseModal from 'components/Modals/BaseModal';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import AudioManager from 'features/Integrations/features/AudioManager/index.web';
import {
  DEVICE_HISTORY_PAGE_SIZE,
  DEVICE_HISTORY_START_MS,
} from 'features/Integrations/config/deviceHistory.config';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { FaChevronDown } from 'react-icons/fa6';
import { PiPencilLineFill } from 'react-icons/pi';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useKillswitchDeviceMutation } from 'services/devices/devices.api';
import { notifyErrorToast } from 'utils/notifications/toast.utils';
import { mergeCommandDraft } from 'features/Integrations/utils/devices.utils';
import MediaPanel from './components/MediaPanel/MediaPanel';
import {
  applyMaintenanceState,
  canSendDeviceCommands,
  getMaintenanceState,
  getMaintenanceStateWithStatus,
  hasMaintenanceStateChanged,
} from './utils/deviceSettings.utils';

const DeviceSettings = ({ device }) => {
  const [killswitchDevice, { isLoading: isKilling }] = useKillswitchDeviceMutation();

  // A stable reference so AudioManager doesn't re-derive its audio list (and
  // refetch every file) on every unrelated re-render, e.g. a resize.
  const audioManagerDeviceIds = useMemo(
    () => (device?.id != null ? [device.id] : []),
    [device?.id],
  );

  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      useRenderTracker('DeviceSettings');
    }
  } catch (e) {}
  const perms = usePerms();

  const [commandDraft, setCommandDraft] = useState(device);
  // The command fields the operator changed since the last successful send.
  const editedFieldsRef = useRef([]);
  const [maintenanceState, setMaintenanceState] = useState(() => getMaintenanceState(device));
  const observedDeviceRef = useRef(device);
  const maintenanceRequestRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);
  // Closed until asked for: with the panel open by default the media view was
  // pushed up and the modal read as a history table (Mike, 2026-08-28).
  const [historyOpen, setHistoryOpen] = useState(false);
  const [stopLiveAudioSignal, setStopLiveAudioSignal] = useState(0);

  // The page load holds ONE row for each device, so this panel would list a single
  // activation. The rest is asked for when the panel is opened, a page at a time;
  // the points merge into the device cache the table below reads. The window starts
  // at the epoch because this panel only exists for command devices, which write a
  // row when a person commands them — see DEVICE_HISTORY_START_MS.
  const historyIds = useMemo(() => (device?.id != null ? [device.id] : []), [device?.id]);
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: historyIds,
    startTime: DEVICE_HISTORY_START_MS,
    limit: DEVICE_HISTORY_PAGE_SIZE,
    enabled: historyOpen,
  });

  const maintenanceActive = Boolean(maintenanceState.killSwitch) || maintenanceState.status === -1;

  useEffect(() => {
    const previousDevice = observedDeviceRef.current;
    if (previousDevice === device) return;
    observedDeviceRef.current = device;

    const isSameDevice = previousDevice.id === device.id;
    const observedMaintenance = getMaintenanceState(
      device,
      isSameDevice ? maintenanceState.previousStatus : 0,
    );
    const hasMaintenanceUpdate = hasMaintenanceStateChanged(previousDevice, device);
    const nextMaintenance = hasMaintenanceUpdate
      ? observedMaintenance
      : getMaintenanceStateWithStatus(maintenanceState, observedMaintenance.previousStatus);

    if (hasMaintenanceUpdate) {
      // The live device shows the result, so a later failure of this request is not reported.
      if (maintenanceRequestRef.current) maintenanceRequestRef.current.isSuperseded = true;
      maintenanceRequestRef.current = null;
    } else if (maintenanceRequestRef.current) {
      // A normal device event updates the rollback baseline, not the pending desired mode.
      maintenanceRequestRef.current.rollbackState = getMaintenanceStateWithStatus(
        maintenanceRequestRef.current.rollbackState,
        observedMaintenance.previousStatus,
      );
    }
    setMaintenanceState(nextMaintenance);
    // History/cache refreshes must not erase pending maintenance or command edits.
    setCommandDraft((draft) => {
      const nextDraft = isSameDevice
        ? mergeCommandDraft(device, draft, editedFieldsRef.current)
        : device;
      return applyMaintenanceState(nextDraft, nextMaintenance);
    });
  }, [device, maintenanceState]);

  useEffect(
    () => () => {
      maintenanceRequestRef.current = null;
    },
    [],
  );

  const onEnableMaintenanceMode = async () => {
    if (!perms.isMaintenanceOperator() || isKilling || maintenanceRequestRef.current) return;

    const nextMaintenance = {
      ...maintenanceState,
      killSwitch: maintenanceActive ? 0 : 1,
      status: maintenanceActive ? maintenanceState.previousStatus : -1,
    };
    const request = { rollbackState: maintenanceState };
    maintenanceRequestRef.current = request;
    setMaintenanceState(nextMaintenance);
    setCommandDraft((draft) => applyMaintenanceState(draft, nextMaintenance));

    try {
      await killswitchDevice({ id: device.id, state: nextMaintenance.killSwitch }).unwrap();
    } catch (err) {
      // The operator may have closed the modal or picked another device while the request
      // was pending. The toast names the device and is still the only sign of the failure.
      if (!request.isSuperseded) {
        notifyErrorToast(`maintenance mode for ${device.alias || 'the device'}`, {
          isAddOperation: false,
        });
      }
      if (maintenanceRequestRef.current !== request) return;
      setMaintenanceState(request.rollbackState);
      // Restore only maintenance fields on the latest draft, never captured command edits.
      setCommandDraft((draft) => applyMaintenanceState(draft, request.rollbackState));
    } finally {
      if (maintenanceRequestRef.current === request) maintenanceRequestRef.current = null;
    }
  };

  const deviceControlListener = async (evt, data, details) => {
    switch (evt) {
      case 'update':
        editedFieldsRef.current = [
          ...new Set([...editedFieldsRef.current, ...(details?.editedFields || [])]),
        ];
        setCommandDraft(data);
        return;
      case 'submit': {
        setSending(true);
        // Edits made while the request is pending are not part of this send.
        const sentFields = editedFieldsRef.current;
        try {
          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments, //does not exist in data
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments || 'No Comment Provided',
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
          // The sent values are now the device's to report; the controls follow it again.
          editedFieldsRef.current = editedFieldsRef.current.filter(
            (field) => !sentFields.includes(field),
          );
        } catch (error) {
          console.error('Error sending device command:', error);
        }
        setSending(false);
        return;
      }
      case 'cancel-audio':
        try {
          await sendDeviceCommand(
            {
              id: device.id,
              node_id: device.node_id,
              system_type: device.system_type,
              device_type: device.device_type,
            },
            7,
            '',
            data?.maintenanceOverride === true,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverride({
      id: -1,
      name: fileName,
      displayName: fileName,
      relay_id: -1,
      device_ids: null,
    });

    setAudioManager(false);
  };

  const hasDeviceLayout = Boolean(layout[device.device_type?.toLowerCase()]);
  const hasMediaPreview = device.device_type?.toLowerCase() === 'overwatch vms' || device.camera;
  const isMediaOnlySettings = hasMediaPreview && !hasDeviceLayout;

  return (
    <div
      className={`d-flex flex-column gap-2 overflow-x-hidden${isMediaOnlySettings ? ' min-h-75vh' : ''}`}
    >
      {/* Top */}
      <div className="d-flex flex-column gap-3">
        <MediaPanel
          device={commandDraft}
          isMediaOnly={isMediaOnlySettings}
          onVideoSelected={() => setStopLiveAudioSignal((previous) => previous + 1)}
        />

        {perms.isMaintenanceOperator() &&
          layout[device.device_type.toLowerCase()] &&
          device.device_type !== 'Manual Road Closure' && (
            <Button
              variant={maintenanceActive ? 'warning' : 'primary'}
              onClick={onEnableMaintenanceMode}
              disabled={isKilling}
            >
              {maintenanceActive ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
            </Button>
          )}

        {canSendDeviceCommands(device, perms) && hasDeviceLayout && (
          <DeviceControls
            device={commandDraft}
            on={deviceControlListener}
            sending={sending}
            audioOverride={audioOverride}
            stopLiveAudioSignal={stopLiveAudioSignal}
          />
        )}

        {/* Audio manager access for camera-enabled sirens — always accessible regardless of device status */}
        {device?.camera &&
          (perms.testPermission([], ['ViewSirenAudioFiles']) ||
            perms.testPermission([], ['CreateSirenAudioFiles']) ||
            perms.testPermission([], ['Edit-DeleteSirenAudioFiles'])) && (
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2"
              onClick={() => setAudioManager(true)}
            >
              <PiPencilLineFill />
              Manage Audio Files
            </Button>
          )}
      </div>

      {/* Bottom: device history panel */}
      {layout[device.device_type.toLowerCase()] && (
        <div className="d-flex flex-column">
          <Button
            type="button"
            variant="primary"
            className="d-flex align-items-center justify-content-center gap-2"
            onClick={() => setHistoryOpen((prev) => !prev)}
            aria-expanded={historyOpen}
            aria-controls="standard-device-history"
          >
            <span>Device History</span>
            <FaChevronDown
              size={12}
              style={{
                transform: historyOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          </Button>

          <Collapse in={historyOpen}>
            <div id="standard-device-history" className="pt-2">
              <DeviceHistory
                title={'Device History'}
                subTitle={device.alias}
                data={device?.data}
                deviceType={device.device_type}
                loading={historyLoading}
                hasMore={historyHasMore}
                onLoadMore={loadMoreHistory}
                error={historyError}
              />
            </div>
          </Collapse>
        </div>
      )}

      {/* Audio Manager */}
      <BaseModal
        contentClassName="overflow-auto"
        contentStyle={{ height: '75dvh' }}
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && device?.camera && audioManager}
        onHide={() => setAudioManager(false)}
        closeButton
        size="lg"
        centered
        stackLevel={2}
      >
        <AudioManager
          deviceIds={audioManagerDeviceIds}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
};

export default DeviceSettings;
